import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Loader2, ArrowLeft, UserPlus, X, MapPin, Trash2 } from "lucide-react";
import GeofenceMapPicker from "@/components/admin/GeofenceMapPicker";

const AUTO_HOURS_OPTIONS = [
  { value: 1, label: "1 hour" },
  { value: 4, label: "4 hours" },
  { value: 12, label: "12 hours" },
  { value: 24, label: "24 hours (default)" },
  { value: 48, label: "48 hours" },
  { value: 72, label: "72 hours" },
];

const ENFORCE_OPTIONS = [
  { value: "always_allow", label: "Always allow", hint: "Manual clock-in doesn't check coords." },
  { value: "only_in_geofence", label: "Only in geofence", hint: "Reject manual clock-in outside radius." },
];

export default function WorksiteDetailPage() {
  const { id } = useParams();
  const [ws, setWs] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [addUserId, setAddUserId] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [wsr, ar, ur] = await Promise.all([
        api.get(`/tenant/worksites/${id}`),
        api.get(`/tenant/worksites/${id}/assignments`),
        api.get(`/tenant/users`),
      ]);
      setWs(wsr.data);
      setAssignments(ar.data.items);
      const assigned = new Set(ar.data.items.map((a) => a.user_id));
      setCandidates(ur.data.items.filter((u) => !assigned.has(u.id) && u.status === "active"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const add = async () => {
    if (!addUserId) return;
    try {
      await api.post(`/tenant/worksites/${id}/assignments`, { user_id: addUserId });
      toast.success("Assigned");
      setAddUserId("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (userId) => {
    try {
      await api.delete(`/tenant/worksites/${id}/assignments/${userId}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="worksite-detail-page">
      <div>
        <Button asChild variant="ghost" size="sm"><Link to="/app/admin/worksites"><ArrowLeft className="h-4 w-4" /> Worksites</Link></Button>
        {ws && (
          <>
            <h1 className="mt-2 font-display text-3xl font-semibold text-primary">{ws.name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-muted-foreground">
              <Badge variant="secondary" className="rounded-full font-mono text-xs">{ws.code}</Badge>
              {ws.address && <span>· {ws.address}</span>}
              <Badge variant={ws.active ? "default" : "outline"} className="ml-2 capitalize">{ws.active ? "active" : "inactive"}</Badge>
            </div>
          </>
        )}
      </div>

      {ws && <GeofenceCard worksite={ws} onSaved={load} />}

      <Card>
        <CardHeader><CardTitle>Add worker</CardTitle></CardHeader>
        <CardContent className="flex flex-col gap-3 sm:flex-row">
          <Select value={addUserId} onValueChange={setAddUserId}>
            <SelectTrigger className="sm:w-96" data-testid="assign-user-select"><SelectValue placeholder="Pick a user…" /></SelectTrigger>
            <SelectContent>
              {candidates.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.full_name || u.email} ({u.email})</SelectItem>
              ))}
              {candidates.length === 0 && <div className="px-2 py-2 text-sm text-muted-foreground">No unassigned active users.</div>}
            </SelectContent>
          </Select>
          <Button onClick={add} disabled={!addUserId} data-testid="assign-user-btn"><UserPlus className="h-4 w-4" /> Assign</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Assigned workers ({assignments.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></TableCell></TableRow>}
              {!loading && assignments.length === 0 && <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">Nobody assigned yet.</TableCell></TableRow>}
              {assignments.map((a) => (
                <TableRow key={a.id} data-testid={`assignment-${a.user_id}`}>
                  <TableCell className="font-medium">{a.user?.full_name || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{a.user?.email}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(a.user?.roles || []).map((r) => <Badge key={r} variant="secondary" className="rounded-full">{r}</Badge>)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => remove(a.user_id)} data-testid={`unassign-${a.user_id}`}><X className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}


function GeofenceCard({ worksite, onSaved }) {
  const initial = worksite.geo || {};
  const [enabled, setEnabled] = useState(Boolean(initial.enabled));
  const [lat, setLat] = useState(initial.lat ?? 40.7128);
  const [lng, setLng] = useState(initial.lng ?? -74.006);
  const [radius, setRadius] = useState(initial.radius_meters ?? 100);
  const [enforce, setEnforce] = useState(initial.enforce_manual_clock_in ?? "always_allow");
  const [autoHours, setAutoHours] = useState(initial.auto_confirm_after_hours ?? 24);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const g = worksite.geo || {};
    setEnabled(Boolean(g.enabled));
    setLat(g.lat ?? 40.7128);
    setLng(g.lng ?? -74.006);
    setRadius(g.radius_meters ?? 100);
    setEnforce(g.enforce_manual_clock_in ?? "always_allow");
    setAutoHours(g.auto_confirm_after_hours ?? 24);
  }, [worksite.id, worksite.geo]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/tenant/worksites/${worksite.id}/geo`, {
        lat: Number(lat), lng: Number(lng),
        radius_meters: Number(radius),
        enforce_manual_clock_in: enforce,
        auto_confirm_after_hours: Number(autoHours),
        enabled,
      });
      toast.success("Geofence saved");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const clearGeo = async () => {
    if (!confirm(`Remove the geofence for ${worksite.name}?`)) return;
    try {
      await api.delete(`/tenant/worksites/${worksite.id}/geo`);
      toast.success("Geofence cleared");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card data-testid="geofence-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-secondary" /> Geofence & auto-clock
        </CardTitle>
        <div className="flex items-center gap-2">
          <Label htmlFor="geo-enabled" className="text-sm text-muted-foreground">Enabled</Label>
          <Switch
            id="geo-enabled"
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid="geofence-enabled-toggle"
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Workers whose device reports coordinates inside the fence are auto clocked-in
          on entry and clocked-out on exit. Entries stay <em>pending</em> until the
          worker confirms — or auto-accept sweeps them after the configured window.
        </p>

        <GeofenceMapPicker
          lat={Number(lat)}
          lng={Number(lng)}
          radius_meters={Number(radius) || 100}
          onChange={({ lat: nlat, lng: nlng }) => {
            setLat(Number(nlat.toFixed(6)));
            setLng(Number(nlng.toFixed(6)));
          }}
          testId="geofence-map-picker"
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="geo-lat">Latitude</Label>
            <Input
              id="geo-lat" type="number" step="0.000001" value={lat}
              onChange={(e) => setLat(e.target.value)}
              data-testid="geofence-lat-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="geo-lng">Longitude</Label>
            <Input
              id="geo-lng" type="number" step="0.000001" value={lng}
              onChange={(e) => setLng(e.target.value)}
              data-testid="geofence-lng-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="geo-radius">Radius (m) · 50–1000</Label>
            <Input
              id="geo-radius" type="number" min={50} max={1000} step={10}
              value={radius}
              onChange={(e) => setRadius(e.target.value)}
              data-testid="geofence-radius-input"
            />
          </div>
        </div>

        <Separator />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Manual clock-in policy</Label>
            <Select value={enforce} onValueChange={setEnforce}>
              <SelectTrigger data-testid="geofence-enforce-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ENFORCE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    <div className="flex flex-col">
                      <span className="font-medium">{o.label}</span>
                      <span className="text-xs text-muted-foreground">{o.hint}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Auto-confirm pending punches after</Label>
            <Select value={String(autoHours)} onValueChange={(v) => setAutoHours(Number(v))}>
              <SelectTrigger data-testid="geofence-auto-hours-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {AUTO_HOURS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2 pt-2">
          {worksite.geo && (
            <Button
              variant="ghost"
              onClick={clearGeo}
              className="text-danger hover:bg-danger/10 hover:text-danger"
              data-testid="geofence-clear-btn"
            >
              <Trash2 className="mr-1.5 h-4 w-4" /> Remove geofence
            </Button>
          )}
          <Button onClick={save} disabled={saving} data-testid="geofence-save-btn">
            {saving ? "Saving…" : worksite.geo ? "Update geofence" : "Save geofence"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
